import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Pagination, Table, Tag, Tooltip, notification } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { Ellipsis, NoWrap } from 'pages/star-shared/components/status-tags';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import type { StudioLogRow } from '../contract';
import { STUDIO_PAGES, StudioPaging, disputeLog } from '../api';
import { mapStudioError } from '../studio-error';
import {
  EMPTY_TEXT,
  REASON_MIN,
  FLAG_SEVERITY_COLOR,
  FLAG_SEVERITY_LABEL,
  FLAG_STATUS_LABEL,
  LINK_RELATION_LABEL,
  METHOD_LABEL,
  TIER_COLOR,
  TIER_LABEL,
  fieldLabel,
  flagLabel,
} from '../labels';
import { formatDateTime } from '../format';
import CloseFlagModal, { CloseFlagTarget } from './close-flag-modal';
import styles from '../styles.module.scss';

export interface LogTableProps {
  rows: StudioLogRow[];
  loading?: boolean;
  /** Cột Studio (Giám sát liên Studio, S30). */
  showStudio?: boolean;
  /** Cột Cờ: ADMIN, SMOD (Quản lý Studio không có cột này; máy chủ cũng trả flags: []). */
  showFlags?: boolean;
  /** ADMIN: bấm cờ OPEN mở modal Khép cờ. */
  canCloseFlag?: boolean;
  onFlagClosed?: () => void;
  /** Sau khi "Báo sai" một dòng (route sẵn có POST /approvals/log/:id/dispute, chỉ dòng `canDispute`). */
  onDisputed?: () => void;
  pagination?: StudioPaging;
  emptyText?: string;
}

const MAX_CHANGES = 4;

/** Bảng StudioLogRow (mục 8.7.9, 8.7.10), dùng ở tab Lịch sử và trang Giám sát. */
const LogTable = ({
  rows,
  loading,
  showStudio,
  showFlags,
  canCloseFlag,
  onFlagClosed,
  onDisputed,
  pagination,
  emptyText = EMPTY_TEXT.log,
}: LogTableProps) => {
  const [closing, setClosing] = useState<CloseFlagTarget | null>(null);
  const [dispute, setDispute] = useState<NoteActionConfig | null>(null);
  const anyDispute = rows.some((r) => r.canDispute);

  // Người được nhắc tới trong dòng báo "sai" (luật cũ `canDispute`, mục 6.4). Ghi chú 5–500 ký tự.
  const openDispute = (row: StudioLogRow) =>
    setDispute({
      title: 'Báo sai dòng lịch sử',
      description: (
        <span>
          {formatDateTime(row.occurredAt)} · {row.actionLabel || row.action}
        </span>
      ),
      noteRequired: true,
      noteLabel: `Điều gì chưa đúng? (ít nhất ${REASON_MIN} ký tự)`,
      confirmText: 'Gửi báo sai',
      onSubmit: async (note) => {
        if ((note || '').length < REASON_MIN) {
          notification.error({ message: `Nội dung cần ít nhất ${REASON_MIN} ký tự.` });
          throw new Error('REASON_TOO_SHORT');
        }
        try {
          await disputeLog(row.id, note as string);
          notification.success({ message: 'Đã gửi báo sai.' });
          onDisputed?.();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          throw err;
        }
      },
    });

  const columns: ColumnsType<StudioLogRow> = [
    {
      title: 'Thời gian',
      key: 'occurredAt',
      width: 150,
      render: (_, r) => <NoWrap>{formatDateTime(r.occurredAt)}</NoWrap>,
    },
    ...(showStudio
      ? ([
          {
            title: 'Studio',
            key: 'studio',
            width: 170,
            render: (_, r) =>
              r.studio ? (
                <div>
                  <Link to={STUDIO_PAGES.detail(r.studio.id)}>
                    {r.studio.title || `Studio #${r.studio.id}`}
                  </Link>
                  <div className={styles.muted}>{LINK_RELATION_LABEL[r.relation]}</div>
                </div>
              ) : (
                '—'
              ),
          },
        ] as ColumnsType<StudioLogRow>)
      : []),
    {
      title: 'Người thao tác',
      key: 'actor',
      width: 170,
      render: (_, r) =>
        r.actor ? (
          <div>
            <div className={styles.cellTitle}>{r.actor.name}</div>
            {r.actor.tier && (
              <Tag color={TIER_COLOR[r.actor.tier]} style={{ marginTop: 2 }}>
                {TIER_LABEL[r.actor.tier]}
              </Tag>
            )}
          </div>
        ) : (
          'Hệ thống'
        ),
    },
    {
      title: 'Việc',
      key: 'action',
      width: 170,
      render: (_, r) => <span className={styles.cellTitle}>{r.actionLabel || r.action}</span>,
    },
    {
      title: 'Người liên quan',
      key: 'subject',
      width: 190,
      render: (_, r) => (
        <div>
          <div>{r.subjectLabel || r.subject?.name || '—'}</div>
          {r.team && <div className={styles.muted}>{r.team.name}</div>}
        </div>
      ),
    },
    {
      title: 'Thay đổi',
      key: 'changes',
      width: 260,
      render: (_, r) => {
        if (!r.changes?.length) return '—';
        const shown = r.changes.slice(0, MAX_CHANGES);
        const rest = r.changes.slice(MAX_CHANGES);
        return (
          <ul className={styles.changes}>
            {shown.map((c) => (
              <li key={c.field}>
                {fieldLabel(c.field)}: <span>{c.from ?? '—'}</span> → {c.to ?? '—'}
              </li>
            ))}
            {rest.length > 0 && (
              <li>
                <Tooltip
                  title={rest.map((c) => (
                    <div key={c.field}>
                      {fieldLabel(c.field)}: {c.from ?? '—'} → {c.to ?? '—'}
                    </div>
                  ))}
                >
                  <span>+{rest.length} thay đổi khác</span>
                </Tooltip>
              </li>
            )}
          </ul>
        );
      },
    },
    {
      title: 'Lý do',
      key: 'reason',
      width: 200,
      render: (_, r) => <Ellipsis value={r.reason} width={200} />,
    },
    {
      title: 'Cách làm',
      key: 'method',
      width: 110,
      render: (_, r) => METHOD_LABEL[r.method] || r.method,
    },
    ...(showFlags
      ? ([
          {
            title: 'Cờ',
            key: 'flags',
            width: 180,
            render: (_, r) =>
              r.flags?.length ? (
                <span className={styles.tags}>
                  {r.flags.map((f) => {
                    const clickable = canCloseFlag && f.status === 'OPEN';
                    const tip = `${FLAG_SEVERITY_LABEL[f.severity]} · ${FLAG_STATUS_LABEL[f.status]}${
                      clickable ? ' · bấm để khép cờ' : ''
                    }`;
                    return (
                      <Tooltip key={f.id} title={tip}>
                        <Tag
                          color={f.status === 'OPEN' ? FLAG_SEVERITY_COLOR[f.severity] : 'default'}
                          style={clickable ? { cursor: 'pointer' } : undefined}
                          onClick={
                            clickable
                              ? () =>
                                  setClosing({
                                    id: f.id,
                                    code: f.code,
                                    label: f.label,
                                    severity: f.severity,
                                  })
                              : undefined
                          }
                        >
                          {flagLabel(f.code, f.label)}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </span>
              ) : (
                '—'
              ),
          },
        ] as ColumnsType<StudioLogRow>)
      : []),
    ...(anyDispute
      ? ([
          {
            title: '',
            key: 'dispute',
            width: 100,
            render: (_, r) =>
              r.canDispute ? (
                <Button size="small" type="link" onClick={() => openDispute(r)}>
                  Báo sai
                </Button>
              ) : null,
          },
        ] as ColumnsType<StudioLogRow>)
      : []),
  ];

  return (
    <div className="custom-table">
      <Table<StudioLogRow>
        columns={columns}
        rowKey="id"
        dataSource={rows}
        loading={loading}
        pagination={false}
        locale={{ emptyText }}
        scroll={{ x: showStudio ? 1500 : 1330 }}
      />
      {pagination && pagination.total > pagination.pageSize && (
        <Pagination
          showSizeChanger={false}
          showLessItems
          current={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={pagination.onChange}
        />
      )}
      <CloseFlagModal
        flag={closing}
        onClose={() => setClosing(null)}
        onDone={() => onFlagClosed?.()}
      />
      <NoteActionModal config={dispute} onClose={() => setDispute(null)} />
    </div>
  );
};

export default LogTable;
